import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {ReactComponent as ExchangeIcon} from '../../../src/assests/svg/icon_shift.svg';
import {ReactComponent as PlusIcon} from '../../assests/svg/plus-icon.svg';
import navConstants from '../../constants/navConstants';
import {setModalStatus} from '../../redux/slices/invoiceModalSlice';
import {RootState} from '../../redux/store';
import AurBreadcrumb from '../common/aur-breadcrumb/AurBreadCrumb';
import AurButton from '../common/aur-button/AurButton';
import {
  AurDropdownButton,
  DropdownIconTypes,
} from '../common/aur-button/AurDropdownButton';
import AurIcon, {IconColors, IconNames} from '../common/aur-icon/AurIcon';
import AurTabMenu, {AurTab} from '../common/aur-tab-menu/AurTabMenu';
import ProjectSwitcherModal from '../ecapages/eca-overview/eca-overlay/ProjectSwitcherModal';
import EcaOverview from '../ecapages/eca-overview/EcaOverview';
import EcaSearchTermReports from '../ecapages/eca-searchterm-reports/EcaSearchTermReports';
import {ReactComponent as CloseIconLight} from '../../assests/svg/close_icon_light.svg';
import AurEcaBanner from '../common/aur-eca-banner/AurEcaBanner';
import {STR_FLOW} from './search-term-report/CreateSTR';
import {setShowExportManagement} from '../../redux/slices/ecaAdhocSearchSlice';
import {
  setEcaDocumentViewerModal,
  setHighLightedWords,
  setShowHighLightedTab,
} from '../../redux/slices/ecaDocumentViewerSlice';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../hooks/permissions/usePermissionsByPage';
import {
  PAGE_TYPE,
  useManageMenuByPermissions,
} from '../../hooks/permissions/useManageMenuByPermissions';
import {
  GA_EVENT_NAMES,
  GA_CLICK_TYPES,
  GA_REGIONS,
} from '../../constants/analyticsConstants';
import {useAnalytics} from '../../hooks/useAnalytics';
import { EciHomePageTabs } from '../../constants/constants';

interface TabItemType extends AurTab {
  type?: EciHomePageTabs;
  name?: string;
}
type ReportTab = {
  index: number;
  content: JSX.Element;
  header: {
    text: string;
  };
  type: EciHomePageTabs;
  onClick?: () => void;
  name?: string;
};

const DEFAULT_SELECTED_TAB = EciHomePageTabs.ECI_OVERVIEW;
const getSelectedTabIndexFromParam = (search: string, tabs: TabItemType[]) => {
  const locationQueryParam = new URLSearchParams(search);
  if (locationQueryParam.get('tab')) {
    return getTabsIndexFromType(locationQueryParam.get('tab') as string, tabs);
  }
  return -1;
};

const getTabsIndexFromType = (type: string, tabs: TabItemType[]) => {
  return tabs.findIndex((item) => item.type === type);
};

const EcaHomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const {hasAIInvestigatePerm, hasSearchTermReportPerm, hasExportPerm} =
    usePermissionsByPage(PAGE_NAME.ECA_HOME);

  const ClientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const {manageMenus: manageDropdownItems} = useManageMenuByPermissions(
    PAGE_TYPE.Home,
    ClientId
  );

  const [selectedTab, setSelectedTab] = useState(-1);

  const {
    selectedProjectName: projectName,
    projectId: selectedProjectId,
    nlpSearchEnabled,
  } = useSelector((state: RootState) => state.reports);

  const tabs: ReportTab[] = useMemo(() => {
    return [
      {
        index: 0,
        content: <EcaOverview />,
        header: {
          text: t('eca.ecaHomepage.ecaOverview'),
        },
        type: EciHomePageTabs.ECI_OVERVIEW,
        name: 'eci-overview',
      },
      ...(hasSearchTermReportPerm
        ? [
            {
              index: 1,
              content: <EcaSearchTermReports />,
              header: {
                text: t('eca.ecaHomepage.searchTerm'),
              },
              type: EciHomePageTabs.STR,
              name: 'search-term-reports',
            },
          ]
        : []),
    ];
  }, [hasSearchTermReportPerm]);

  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs[selectedTab]) {
      setSelectedTab(0);
    }
  }, [tabs, selectedTab, navigate]);

  useEffect(() => {
    setInitialSelectedTabOnLoad(tabs);
    dispatch(setEcaDocumentViewerModal(false));
    dispatch(setHighLightedWords([]));
    dispatch(setShowHighLightedTab(false));
  }, []);

  useEffect(() => {
    dispatch(setShowExportManagement(hasExportPerm ?? false));
  }, [dispatch, hasExportPerm]);

  useEffect(() => {
    const wrapper = document.querySelector<HTMLElement>('.osano-cm-window');
    wrapper?.setAttribute('role', 'region');
    wrapper?.setAttribute('aria-label', t('common.footer.cookiePreferences'));
  }, []);

  const handleTabChange = (index: number) => {
    setSelectedTab(index);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: tabs[index].header.text,
        type: GA_CLICK_TYPES.TAB,
        region: GA_REGIONS.BODY,
        action: `${window.location.host + window.location.pathname}?tab=${tabs[index].name}`
      }
    })
    navigate(`/eci-home-page?tab=${tabs[index].type}`);
  };

  const setInitialSelectedTabOnLoad = (tabs: TabItemType[]) => {
    const getTabIndex = getSelectedTabIndexFromParam(location.search, tabs);

    if (getTabIndex !== -1) {
      setSelectedTab(getTabIndex);
    } else {
      const defaultTabIndex = getTabsIndexFromType(DEFAULT_SELECTED_TAB, tabs);
      setSelectedTab(defaultTabIndex !== -1 ? defaultTabIndex : 0);
    }
  };

  const selectedTabType = useMemo(() => {
    return tabs[selectedTab]?.type ?? null;
  }, [selectedTab, tabs]);

  const breadCrumbItems = useMemo(() => {
    const baseItems: {label: string; path?: string; labelColor?: string}[] = [
      {
        label: t('reports.projectOverview.label'),
        path: '/welcome-single-client',
      },
      {
        label: t('eca.ecaHomepage.coreEciHome'),
        labelColor: '#CCD5E3',
      },
    ];
    return baseItems;
  }, [selectedTab, tabs]);

  const ProjectAndManageButtons = () => {
    return (
      <Box
        className="client-profile-right"
        display="flex"
        justifyContent="space-between"
        gap={2}
        alignItems="center"
      >
        {selectedTabType === EciHomePageTabs.STR && (
          <AurButton
            type="continue"
            size="Medium"
            label={t('eca.searchTermReport.createNewSTRButton')}
            startIcon={<PlusIcon aria-hidden />}
            onClick={() => {
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('eca.searchTermReport.createNewSTRButton'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.BODY,
                  action:
                    window.location.host +
                    navConstants.str.baseSTR +
                    `?Flow=${STR_FLOW.CREATE}`,
                },
              });
              navigate(navConstants.str.baseSTR + `?Flow=${STR_FLOW.CREATE}`);
            }}
            dataTestId="createNewSTRButton"
            ariaDescribedBy="str-title"
          />
        )}

        {manageDropdownItems?.length > 0 && (
          <AurDropdownButton
            buttonText={t('eca.ecaHomepage.manage')}
            type="continue"
            children={
              <AurIcon
                icon={IconNames.ChevronDown}
                color={IconColors.Custom}
                customClassName="client-profile-details-dropdown-icon"
              />
            }
            aria-live="polite"
            size="Medium"
            iconType="Arrow"
            iconSize="medium"
            iconClassName="client-profile-details-dropdown-icon"
            menuItemCustomWidth="255px"
            menuItemHeight="medium"
            menuItemWidth="mediumLarge"
            menuItemTextSize="small"
            dropDownIconType={DropdownIconTypes.Chev}
            // This is Temporary, after API implementation will remove
            menuItems={manageDropdownItems}
            menuClassNames="manage-dropdown-menu"
          />
        )}
        <AurButton
          ariaLabel={t('reports.inTakeAuditTab.header.projectButton')+'-'+t(
            'reports.inTakeAuditTab.header.projectWorkspaceButtonHint'
          )}
          classNames="typo-text buttonMedium_size"
          onClick={() => {
            dispatch(setModalStatus(true));
          }}
          type="continue"
          size="Medium"
          startIcon={<ExchangeIcon aria-hidden />}
          dataTestId="open-project-workspace-dialog"
        >
          {t('reports.inTakeAuditTab.header.projectButton')}
        </AurButton>
      </Box>
    );
  };

  return (
    <div
      className={`project-overview-main  ${selectedTabType === EciHomePageTabs.STR ? 'header-glow-bg-img' : 'eca-bg'}`}
    >
      <Box
        className="breadcrumb-section eca-breadcrumb"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <AurBreadcrumb items={breadCrumbItems} />
        {hasAIInvestigatePerm && nlpSearchEnabled && (
          <AurEcaBanner
            className="eca-home-page-banner"
            nlpBackLabel={t('indexSearchPage.back')}
          />
        )}
        {selectedTabType === EciHomePageTabs.ECI_OVERVIEW && (
          <ProjectAndManageButtons />
        )}
      </Box>

      {selectedTabType === EciHomePageTabs.STR && (
        <div className="eca-searchterm-report-header-main">
          <Typography className="typo-text subhead3_size">
            {projectName}
          </Typography>
          <div className="eca-searchterm-report-header-sub">
            <Typography className="typo-text display3_size" component="h1" id="str-title">
              {t('eca.searchTermReport.STR')}
            </Typography>

            <ProjectAndManageButtons />
          </div>
        </div>
      )}

      <div
        className={`eca-home-page-tab ${selectedTabType === EciHomePageTabs.STR ? '' : 'ecaTab-header'}`}
      >
        <AurTabMenu
          headerClasses="typo-text subhead4_size reports-menu-header"
          isResponsive={true}
          width="100%"
          tabs={tabs}
          title="report"
          defaultTab={selectedTab}
          onTabChange={handleTabChange}
        />
      </div>
      <ProjectSwitcherModal
        className="eca-project-switcher-dialog"
        headingClass="heading-label"
        title={t('eca.ecaHomepage.projectSwitchTitle')}
        heading={t('eca.ecaHomepage.projectSwitchHeading')}
        initialSelectedProjectId={selectedProjectId ?? undefined}
        closeIcon={<CloseIconLight aria-hidden />}
      />
    </div>
  );
};

export default EcaHomePage;
